<template lang="pug">
.window-manager
	slot(name="dock")
		button.minimized-window(v-for="win, index in minimizedWindows" :key="'min__' + index"
			@click="toggleMinimized(index, $event)"
			:title="'Restore ' + win.title + ' Window'"
			)
			| {{ win.title }}
			i.fa.fa-window-restore
	slot
		window(v-for="win, index in openWindows" v-bind="win" :key="index"
			@minimize="toggleMinimized(index, $event)"
			@maximize="toggleMaximized(index, $event)"
			@move="moveWindow(index, $event)"
			@resize="resizeWindow(index, $event)"
			@request:active="activateWindow(index, $event)"
			@close="closeWindow(index, $event)"
			)
</template>

<script>
import Component from "vue-class-component";
import Vue from "vue";
import _ from "lodash";
import Window from "@/components/Window";

@Component({
	props: {
		windows: {
			default: () => {}
		},

		activeWindowZIndexOffset: { default: 100 }
	},
	components: {
		Window
	}
})
class WindowManager extends Vue {
	activeWindowId = null;
	windowStack = [];
	constructor() {
		super();
		console.debug(
			"Window Manager Mounted",
			this,
			this.windows,
			this.openWindows
		);
	}

	get minimizedWindows() {
		return _.pickBy(this.windows, win => win.minimized);
	}

	get openWindows() {
		const activeWindowId = this.activeWindowId;
		return _.mapValues(this.windows, (win, index) => {
			win.selfManaged = false; // this window manager instance is managing them
			win.windowId = index;
			win.active = index == activeWindowId;
			win.activeOpacity = 1.0;
			win.zIndex = this.windowZindex[index] || 0;
			win.activeZIndex = this.windowStack.length + 100;
			return win;
		});
	}

	get windowZindex() {
		const ret = {};
		this.windowStack.forEach((w, i) => (ret[w] = i + 1));
		return ret;
	}

	toggleMaximized(windowId) {
		const win = this.windows[windowId];
		Vue.set(win, "maximized", !win.maximized);
		console.debug("WindowManager.toggleMaximized", win.maximized);
		this.$emit("window:maximize", windowId);
	}

	unminimize(windowId) {
		const win = this.windows[windowId];
		if (win.minimized) this.toggleMinimized(windowId);
	}

	toggleMinimized(windowId) {
		const win = this.windows[windowId];
		console.debug(
			"WindowManager.toggleMinimized",
			win,
			windowId,
			this.windows,
			this.openWindows
		);
		Vue.set(win, "minimized", !win.minimized);
		if (!win.minimized) Vue.nextTick(() => this.activateWindow(windowId));
		this.$emit("window:minimize", windowId);
	}

	moveWindow(windowId, ev) {
		this.windows[windowId].x = ev.x;
		this.windows[windowId].y = ev.y;
		this.$emit("window:move", { id: windowId, event: ev });
	}

	resizeWindow(windowId, ev) {
		this.windows[windowId].width = ev.w;
		this.windows[windowId].height = ev.h;
		this.$emit("window:resize", { id: windowId, event: ev });
	}

	closeWindow(index) {
		Vue.delete(this.windows, index);
		this.$emit("window:close", index);
	}

	activateWindow(index) {
		if (!index || this.activeWindowId === index) return;
		this.activeWindowId = index;
		console.debug("Activating Window ID", this.activeWindowId);
		const lastStackPos = this.windowStack.findIndex(w => w == index);
		if (lastStackPos >= 0) {
			this.windowStack.push(index);
		} else {
			Vue.set(this, "windowStack", [
				...this.windowStack.slice(0, lastStackPos),
				...this.windowStack.slice(lastStackPos + 1),
				index
			]);
		}
		this.$emit("window:activate", index);
	}
}

export default WindowManager;
</script>

<style lang="stylus">
.window-manager
	z-index 1010
	position fixed
	bottom 0
	right 0
	overflow visible

	.minimized-window
		cursor pointer
		border-radius 0
		background #ddd
		color inherit
		border solid 0.1rem #888
		border-bottom 0
		padding 0.5em
		margin 0.25em
		margin-bottom 0

		i.fa
			margin-left 0.5em

.window:hover
	opacity 1
</style>
